"use client";

import { useEffect, useRef } from "react";
import "leaflet/dist/leaflet.css";
import { buildPlaceSlugMap, type Place, type PlaceCategory } from "@/lib/places";

// Default center when the map first loads — Valparai, Tamil Nadu — rather
// than the geometric average of every place's coordinates, which lands
// somewhere between far-apart cities/states and isn't anywhere useful.
const DEFAULT_CENTER: [number, number] = [10.327, 76.955];
const DEFAULT_ZOOM = 13;

// Raw Leaflet (not react-leaflet) initialized imperatively in a client-only
// effect — this is a static-exported site, so anything touching
// `window`/`document` has to run post-mount rather than during render.
// Tiles are OpenStreetMap's free public tile server (no API key, no
// billing) — the standard "free map" choice for a site this size.
export default function PlacesMap({ places, categories }: { places: Place[]; categories: PlaceCategory[] }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<import("leaflet").Map | null>(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const located = places.filter(
      (p) => Number.isFinite(p.latitude) && Number.isFinite(p.longitude)
    );
    if (!located.length) return;

    const slugMap = buildPlaceSlugMap(places);
    let cancelled = false;

    (async () => {
      const L = (await import("leaflet")).default;
      if (cancelled || !containerRef.current || mapRef.current) return;

      const map = L.map(containerRef.current, {
        scrollWheelZoom: true,
      }).setView(DEFAULT_CENTER, DEFAULT_ZOOM);
      mapRef.current = map;

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19,
      }).addTo(map);

      located.forEach((p) => {
        const href = `/places/${slugMap.get(p.placeId) ?? p.placeId}`;
        const categoryIcon = categories.find((c) => c.categoryId === p.category)?.icon ?? "📍";
        const verifiedBadge = p.isVerifiedByAdmin
          ? '<span style="color:#16a34a;font-weight:700;">✓</span>'
          : "";

        // A pill-shaped label (icon + name + verified check) on a short leg
        // pointing at the exact coordinate, instead of a plain dot — lets
        // someone identify a place at a glance instead of having to tap
        // every dot to see what it is. The whole pill is a real <a> so one
        // click/tap opens the place page directly (no popup step). Sized
        // with a zero-size anchor div (iconSize/iconAnchor both [0, 0]) so
        // Leaflet positions everything by CSS around that single point
        // instead of needing a fixed width for the (name-length-dependent)
        // pill.
        const icon = L.divIcon({
          className: "",
          html: `
            <div style="position:relative;width:0;height:0;">
              <a href="${href}" style="position:absolute;bottom:12px;left:0;transform:translateX(-50%);display:inline-flex;align-items:center;gap:4px;padding:6px 10px;border-radius:9999px;background:#ffffff;border:2px solid #16a34a;box-shadow:0 2px 6px rgba(0,0,0,0.18);font-size:12px;font-weight:600;color:#0f172a;white-space:nowrap;text-decoration:none;">
                <span>${categoryIcon}</span><span>${escapeHtml(p.name)}</span>${verifiedBadge}
              </a>
              <span style="position:absolute;top:-12px;left:-1px;width:2px;height:8px;background:#16a34a;"></span>
              <span style="position:absolute;top:-4px;left:-4px;width:8px;height:8px;border-radius:9999px;background:#16a34a;border:2px solid #ffffff;box-shadow:0 1px 3px rgba(0,0,0,0.3);"></span>
            </div>
          `,
          iconSize: [0, 0],
          iconAnchor: [0, 0],
        });

        L.marker([p.latitude, p.longitude], { icon }).addTo(map);
      });

      // If the browser grants location access, re-center on the visitor's
      // actual position instead of the Valparai default — silently keeps
      // the default on denial/timeout/unsupported rather than prompting an
      // error, since this is a nice-to-have, not a requirement to use the map.
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            if (cancelled || mapRef.current !== map) return;
            map.setView([pos.coords.latitude, pos.coords.longitude], DEFAULT_ZOOM);
          },
          () => {},
          { timeout: 8000, maximumAge: 5 * 60 * 1000 }
        );
      }
    })();

    return () => {
      cancelled = true;
      mapRef.current?.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={containerRef}
      className="h-[420px] w-full overflow-hidden rounded-2xl border border-slate-200/80 dark:border-slate-800"
    />
  );
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));
}
